<template>
  <div class="editor-container spacing-py-20 spacing-px-10">
    <b-loading isFullPage :active="isLoading" />
    <span class="has-text-weight-semibold is-size-4 ml-5">
      {{
        id
          ? App.OfficialModelsScreen.DOCUMENT_MODEL_EDITION
          : App.OfficialModelsScreen.DOCUMENT_MODEL_CREATION
      }}
    </span>
    <div class="document-header px-20 py-5">
      <ValidationObserver
        v-slot="{ handleSubmit }"
        tag="div"
        class="flex-1"
        :class="{ 'document-info': isMobile }"
      >
        <div class="user-info">
          <ValidationProvider
            v-slot="{ errors }"
            rules="required|max:70"
            class="column is-5"
            :class="{ 'is-full': isMobile }"
          >
            <b-field
              customClass="has-text-color-7"
              :type="
                isContrast
                  ? 'is-white'
                  : errors.length
                  ? 'is-refused'
                  : 'is-blue-oil'
              "
            >
              <template #label>
                <span class="has-text-weight-semibold">
                  {{ App.OfficialModelsScreen.MODEL_NAME }}
                </span>
                <span class="ml-5 has-text-weight-normal">
                  {{ `(${General.MANDATORY})` }}
                </span>
              </template>
              <b-icon
                v-if="!isMobile"
                icon="file-document"
                :type="isContrast ? 'is-white' : 'is-blue-oil'"
                class="mr-5 pt-10"
                size="mdi-30px"
              />
              <b-input
                v-model="model.name"
                :placeholder="General.PLACEHOLDER.MODEL_NAME"
                :class="{ 'same-line-counter': showNameCounter }"
                :hasCounter="showNameCounter"
                maxlength="70"
                expanded
              />
              <template #message>
                <ErrorField :errors="errors" />
              </template>
            </b-field>
          </ValidationProvider>
          <ValidationProvider
            v-slot="{ errors }"
            rules="required|max:70"
            class="column is-7"
            :class="{ 'is-full': isMobile }"
          >
            <b-field
              customClass="has-text-color-7"
              :type="
                isContrast
                  ? 'is-white'
                  : errors.length
                  ? 'is-refused'
                  : 'is-blue-oil'
              "
            >
              <template #label>
                <span class="has-text-weight-semibold">
                  {{ App.OfficialModelsScreen.MODEL_SMALL_NAME }}
                </span>
                <span class="ml-5 has-text-weight-normal">
                  {{ `(${General.MANDATORY})` }}
                </span>
              </template>
              <div class="small-name-container">
                <b-input
                  v-model="model.short_name"
                  class="custom-input-width"
                  :placeholder="General.PLACEHOLDER.MODEL_NAME_REDUCED"
                  :class="{ 'same-line-counter': showShortCounter }"
                  :hasCounter="showShortCounter"
                  maxlength="70"
                  expanded
                />
                <b-switch
                  v-model="useType"
                  :class="{ 'ml-10': !isMobile }"
                  trueValue="obligatory"
                  falseValue="optional"
                  outlined
                >
                  {{ App.OfficialModelsScreen.MODEL_OBLIGATORY_USE }}
                </b-switch>
              </div>
              <template #message>
                <ErrorField :errors="errors" />
              </template>
            </b-field>
          </ValidationProvider>
        </div>
        <div class="actions">
          <b-button
            :label="General.CLONE_MODELS"
            :type="isContrast ? 'is-white' : 'is-green-lawn'"
            outlined
            :disabled="isLoading"
            @click="confirmActionChangeModel"
          />
          <ValidationProvider v-slot="{ errors }" rules="required">
            <b-field
              :type="
                isContrast
                  ? 'is-white'
                  : errors.length
                  ? 'is-refused'
                  : 'is-blue-oil'
              "
            >
              <b-dropdown
                v-model="typeModel"
                aria-role="list"
                position="is-bottom-right"
                class="file-type-dropdown"
                scrollable
                :maxHeight="200"
                :expanded="isMobile"
              >
                <b-button
                  slot="trigger"
                  slot-scope="{ active }"
                  :type="isContrast ? 'is-white' : 'is-blue-oil'"
                  :iconRight="active ? 'chevron-up' : 'chevron-down'"
                  :expanded="isMobile"
                  outlined
                >
                  {{
                    typeModel && typeModel.type
                      ? typeModel.type
                      : App.ModelsScreen.SELECT_A_DOCUMENT_TYPE
                  }}
                </b-button>

                <b-dropdown-item
                  v-for="type in getFileTypes"
                  :key="type.id"
                  class="custom-dropdown-color"
                  aria-role="listitem"
                  @click="onChangeType(type)"
                >
                  {{ type.type }}
                </b-dropdown-item>
              </b-dropdown>
              <template #message>
                <ErrorField :errors="errors" />
              </template>
            </b-field>
          </ValidationProvider>
          <b-button
            :label="General.CONFIG"
            :type="isContrast ? 'is-white' : 'is-green-lawn'"
            outlined
            @click="showApplicabilityModal"
          />
          <b-button
            :label="General.SAVE"
            class="button-size"
            :type="isContrast ? 'is-white' : 'is-green-lawn'"
            :disabled="isLoading"
            @click="handleSubmit(onSubmitDocument)"
          />
          <b-button
            :label="General.CANCEL"
            class="button-size"
            :type="isContrast ? 'is-white' : 'is-warning'"
            outlined
            @click="cancel"
          />
        </div>
      </ValidationObserver>
    </div>
    <div class="main">
      <vue-file-toolbar-menu :content="menu" class="bar" />
      <section class="main-container">
        <vue-document-editor
          ref="editor"
          class="editor"
          :content.sync="content"
          :overlay="overlay"
          :zoom="responsiveZoom"
          :page_format_mm="page_format_mm"
          :page_margins="page_margins"
          display="vertical"
        />
      </section>
    </div>
  </div>
</template>

<script>
import VueFileToolbarMenu from "vue-file-toolbar-menu";
import VueDocumentEditor from "vue-document-editor";

import { General, App } from "@/utils/constants";
import { isEmpty } from "lodash";

import ModalWarning from "@/views/App/ModalWarning";
import ModalAddInputDoeVue from "@/views/App/ModalAddInputDoe";
import ModalAddSelectDoe from "@/views/App/ModalAddSelectDoe";
import ModalEditorApplicabilityDoe from "@/views/App/ModalEditorApplicabilityDoe";
import ModalOfficialModels from "@/views/App/ModalOfficialModels";
import ModalAddTableDoe from "@/views/App/ModalAddTableDoe";
import ViewEdowebModel from "@/views/App/ViewEdowebModel";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "DoeModelEditor",

  components: { VueFileToolbarMenu, VueDocumentEditor },

  props: {
    id: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      App,
      General,
      isLoading: false,
      content: [``],
      zoom: 0.8,
      zoom_min: 0.1,
      zoom_max: 5.0,
      page_format_mm: [210, 297],
      page_margins: "10mm 15mm",
      display: "grid",
      mounted: false,
      undo_count: -1,
      content_history: [],
      documentInputList: [],
      typeModel: null,
      model: {
        name: "",
        short_name: "",
        nature: "",
        parts: []
      },
      useType: "optional"
    };
  },

  computed: {
    ...mapGetters({
      listDocumentType: "getEdowebDocument",
      editDocument: "getCurrentDocumentEdoweb",
      getFileTypes: "getFileTypes",
      getFileType: "getFileType",
      isContrast: "isContrast",
      isMobile: "isMobile"
    }),
    replaceContent() {
      const newContent = this.content.map(
        page => `<div style="margin: ${this.page_margins};">${page}</div>`
      );

      this.documentInputList.forEach(item => {
        this.content.forEach((_, index) => {
          const newPage = newContent[index].replaceAll(
            `{${item.name}}`,
            item.value
          );
          newContent[index] = newPage;
        });
      });

      return newContent;
    },
    responsiveZoom() {
      return 1.2;
    },
    menu() {
      return [
        {
          text: this.App.EDOWebEditor.CLEAN,
          title: this.App.EDOWebEditor.CLEAN,
          icon: "description",
          click: () => {
            this.$buefy.modal.open({
              parent: this,
              component: ModalWarning,
              props: {
                title: `${this.General.WARNING}!`,
                message: `${General.MESSAGES.CREATE_EMPTY_DOCUMENT}?`,
                confirm: async () => {
                  this.content = [this.getFileType.html_header];
                  this.resetContentHistory();
                },
                buttons: [
                  { label: General.YES, isConfirm: true, action: true },
                  { label: General.NO, isConfirm: false, action: false }
                ]
              },
              hasModalCard: true,
              trapFocus: true
            });
          }
        },
        { is: "separator" },
        {
          title: this.App.EDOWebEditor.FIELDS,
          text: this.App.EDOWebEditor.FIELDS,
          icon: "integration_instructions",
          disabled: !this.current_text_style,
          class: !this.current_text_style ? "toolbar-label" : "",
          menu: [
            {
              text: this.App.EDOWebEditor.OPENED_FIELD,
              click: () => this.openInputModal()
            },
            {
              text: this.App.EDOWebEditor.DROPDOWN_LIST,
              click: () => this.openSelectModal()
            },
            {
              text: this.App.EDOWebEditor.TABLE,
              click: () => this.openTableModal()
            },
            {
              text: this.App.EDOWebEditor.FIELDS_CREATED,
              menu: this.documentInputList.length
                ? this.documentInputList.map(input => ({
                    text: input.name,
                    click: () => this.addField(input)
                  }))
                : false,
              title: !this.documentInputList.length && General.NO_RESULTS,
              menu_class: "align-left"
            }
          ],
          menu_width: 180,
          menu_class: "align-right"
        },
        { is: "separator" },
        {
          title: this.App.EDOWebEditor.UNDO,
          icon: "undo",
          disabled: !this.can_undo,
          hotkey: this.isMacLike ? "command+z" : "ctrl+z",
          click: () => this.undo()
        },
        {
          title: this.App.EDOWebEditor.REDO,
          icon: "redo",
          disabled: !this.can_redo,
          hotkey: this.isMacLike ? "shift+command+z" : "ctrl+y",
          click: () => this.redo()
        },
        { is: "separator" },
        {
          icon: "format_align_left",
          title: this.App.EDOWebEditor.ALIGN_LEFT,
          active: this.isLeftAligned,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? "shift+command+l" : "ctrl+shift+l",
          click: () => document.execCommand("justifyLeft")
        },
        {
          icon: "format_align_center",
          title: this.App.EDOWebEditor.ALIGN_CENTER,
          active: this.isCentered,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? "shift+command+e" : "ctrl+shift+e",
          click: () => document.execCommand("justifyCenter")
        },
        {
          icon: "format_align_right",
          title: this.App.EDOWebEditor.ALIGN_RIGHT,
          active: this.isRightAligned,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? "shift+command+r" : "ctrl+shift+r",
          click: () => document.execCommand("justifyRight")
        },
        {
          icon: "format_align_justify",
          title: this.App.EDOWebEditor.JUSTIFY_CONTENT,
          active: this.isJustified,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? "shift+command+j" : "ctrl+shift+j",
          click: () => document.execCommand("justifyFull")
        },
        { is: "separator" },
        {
          icon: "format_bold",
          title: this.App.EDOWebEditor.BOLD,
          active: this.isBold,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? "command+b" : "ctrl+b",
          click: () => document.execCommand("bold")
        },
        {
          icon: "format_italic",
          title: this.App.EDOWebEditor.ITALIC,
          active: this.isItalic,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? "command+i" : "ctrl+i",
          click: () => document.execCommand("italic")
        },
        {
          icon: "format_underline",
          title: this.App.EDOWebEditor.UNDERLINE,
          active: this.isUnderline,
          disabled: !this.current_text_style,
          hotkey: this.isMacLike ? "command+u" : "ctrl+u",
          click: () => document.execCommand("underline")
        },
        {
          icon: "format_strikethrough",
          title: this.App.EDOWebEditor.STRIKE_THROUGH,
          active: this.isStrikeThrough,
          disabled: !this.current_text_style,
          click: () => document.execCommand("strikethrough")
        },
        { is: "separator" },
        {
          icon: "format_list_numbered",
          title: this.App.EDOWebEditor.NUMBERED_LIST,
          active: this.isNumberedList,
          disabled: !this.current_text_style,
          click: () => document.execCommand("insertOrderedList")
        },
        {
          icon: "format_list_bulleted",
          title: this.App.EDOWebEditor.BULLETED_LIST,
          active: this.isBulletedList,
          disabled: !this.current_text_style,
          click: () => document.execCommand("insertUnorderedList")
        },
        { is: "separator" },
        {
          html: `<b>${this.App.EDOWebEditor.NORMAL}</b>`,
          title: `${this.App.EDOWebEditor.NORMAL}`,
          active: this.isNormal,
          disabled: !this.current_text_style,
          class: !this.current_text_style ? "toolbar-label" : "",
          click: () => this.setTextHeading("div")
        },
        {
          html: `<b>${this.App.EDOWebEditor.TITLE}</b>`,
          title: `${this.App.EDOWebEditor.TITLE}`,
          active: this.isH1,
          disabled: !this.current_text_style,
          class: !this.current_text_style ? "toolbar-label" : "",
          click: () => this.setTextHeading("h1")
        },
        {
          html: `<b>${this.App.EDOWebEditor.TITLE} 2</b>`,
          title: `${this.App.EDOWebEditor.TITLE} 2`,
          active: this.isH2,
          disabled: !this.current_text_style,
          class: !this.current_text_style ? "toolbar-label" : "",
          click: () => this.setTextHeading("h2")
        },
        { is: "separator" },
        {
          icon: "splitscreen",
          title: this.App.EDOWebEditor.PAGE_BREAK,
          disabled: !this.current_text_style,
          click: () => this.insertPageBreak()
        },
        { is: "separator" },
        {
          text: this.current_margins_name,
          title: this.App.EDOWebEditor.MARGIN,
          icon: "select_all",
          chevron: true,
          menu: this.margins.map(([text, value]) => {
            return {
              text: text + " (" + value + ")",
              active: this.page_margins == value,
              click: () => {
                this.page_margins = value;
              }
            };
          }),
          menu_width: 200,
          menu_class: "align-center"
        },
        { is: "separator" },
        {
          text: this.General.TEXTS.VIEW_MODEL,
          title: this.General.TEXTS.VIEW_MODEL,
          click: () => this.viewDocument()
        }
      ];
    },
    current_format_name() {
      const format = this.formats.find(
        ([, width_mm, height_mm]) =>
          this.page_format_mm[0] == width_mm &&
          this.page_format_mm[1] == height_mm
      );
      return format
        ? format[0]
        : this.page_format_mm[0] + "mm x " + this.page_format_mm[1] + "mm";
    },
    formats: () => [
      ["A0", 841, 1189],
      ["A0L", 1189, 841],
      ["A1", 594, 841],
      ["A1L", 841, 594],
      ["A2", 420, 594],
      ["A2L", 594, 420],
      ["A3", 297, 420],
      ["A3L", 420, 297],
      ["A4", 210, 297],
      ["A4L", 297, 210],
      ["A5", 148, 210],
      ["A5L", 210, 148],
      ["A6", 105, 148],
      ["A6L", 148, 105]
    ],
    current_margins_name() {
      const margins = this.margins.find(
        ([, margins]) => this.page_margins == margins
      );
      return margins ? margins[0] : margins[1];
    },
    margins: () => [
      [App.EDOWebEditor.MEDIUM, "20mm"],
      [App.EDOWebEditor.SMALL, "15mm"],
      [App.EDOWebEditor.FINE, "10mm 15mm"]
    ],

    current_text_style() {
      return this.mounted ? this.$refs.editor.current_text_style : false;
    },
    isLeftAligned() {
      return ["start", "left", "-moz-left"].includes(
        this.current_text_style.textAlign
      );
    },
    isRightAligned() {
      return ["end", "right", "-moz-right"].includes(
        this.current_text_style.textAlign
      );
    },
    isCentered() {
      return ["center", "-moz-center"].includes(
        this.current_text_style.textAlign
      );
    },
    isJustified() {
      return ["justify", "justify-all"].includes(
        this.current_text_style.textAlign
      );
    },
    isBold() {
      const fontWeight = this.current_text_style.fontWeight;
      return (
        fontWeight &&
        (parseInt(fontWeight) > 400 || fontWeight.indexOf("bold") === 0)
      );
    },
    isItalic() {
      return this.current_text_style.fontStyle == "italic";
    },
    isUnderline() {
      const stack = this.current_text_style.textDecorationStack;
      return stack && stack.some(d => d.indexOf("underline") == 0);
    },
    isStrikeThrough() {
      const stack = this.current_text_style.textDecorationStack;
      return stack && stack.some(d => d.indexOf("line-through") == 0);
    },
    isNumberedList() {
      return (
        this.current_text_style.isList &&
        this.current_text_style.listStyleType == "decimal"
      );
    },
    isBulletedList() {
      return (
        this.current_text_style.isList &&
        ["disc", "circle"].includes(this.current_text_style.listStyleType)
      );
    },
    isNormal() {
      return (
        this.current_text_style.fontWeight === "400" &&
        this.current_text_style.textAlign !== "justify"
      );
    },
    isH1() {
      return (
        this.current_text_style.fontWeight === "700" &&
        this.current_text_style.textAlign === "center"
      );
    },
    isH2() {
      return (
        this.current_text_style.fontWeight === "400" &&
        this.current_text_style.textAlign === "center"
      );
    },
    curColor() {
      return this.current_text_style.color || "transparent";
    },

    isMacLike: () => /(Mac|iPhone|iPod|iPad)/i.test(navigator.userAgent),

    can_undo() {
      return this.undo_count > 0;
    },
    can_redo() {
      return this.content_history.length - this.undo_count - 1 > 0;
    },
    showNameCounter() {
      return this.model.name.length == 70;
    },
    showShortCounter() {
      return this.model.short_name.length == 70;
    }
  },

  watch: {
    content: {
      handler(new_content) {
        if (!this._mute_next_content_watcher) {
          this.content_history[++this.undo_count] = new_content;
          this.content_history.length = this.undo_count + 1;
        }
        this._mute_next_content_watcher = false;
      }
    }
  },

  created() {
    let start_zoom_gesture = false;
    let start_dist_touch = false;
    let start_zoom_touch = false;

    window.addEventListener(
      "wheel",
      e => {
        if (e.ctrlKey) {
          e.preventDefault();
          this.zoom = Math.min(
            Math.max(this.zoom - e.deltaY * 0.01, this.zoom_min),
            this.zoom_max
          );
        }
      },
      { passive: false }
    );
    window.addEventListener("gesturestart", e => {
      e.preventDefault();
      start_zoom_gesture = this.zoom;
    });
    window.addEventListener("gesturechange", e => {
      e.preventDefault();
      if (!start_zoom_touch) {
        this.zoom = Math.min(
          Math.max(start_zoom_gesture * e.scale, this.zoom_min),
          this.zoom_max
        );
      }
    });
    window.addEventListener("gestureend", () => {
      start_zoom_gesture = false;
    });

    window.addEventListener(
      "touchstart",
      e => {
        if (e.touches.length == 2) {
          e.preventDefault();
          start_dist_touch = Math.hypot(
            e.touches[0].pageX - e.touches[1].pageX,
            e.touches[0].pageY - e.touches[1].pageY
          );
          start_zoom_touch = this.zoom;
        }
      },
      { passive: false }
    );
    window.addEventListener(
      "touchmove",
      e => {
        if (start_dist_touch && start_zoom_touch) {
          e.preventDefault();
          const zoom =
            (start_zoom_touch *
              Math.hypot(
                e.touches[0].pageX - e.touches[1].pageX,
                e.touches[0].pageY - e.touches[1].pageY
              )) /
            start_dist_touch;
          this.zoom = Math.min(Math.max(zoom, this.zoom_min), this.zoom_max);
        }
      },
      { passive: false }
    );
    window.addEventListener(
      "touchend",
      () => {
        start_dist_touch = false;
        start_zoom_touch = false;
      },
      { passive: false }
    );
    const manage_undo_redo = e => {
      switch (e && e.inputType) {
        case "historyUndo":
          e.preventDefault();
          e.stopPropagation();
          this.undo();
          break;
        case "historyRedo":
          e.preventDefault();
          e.stopPropagation();
          this.redo();
          break;
      }
    };

    window.addEventListener("input", manage_undo_redo);
  },

  mounted() {
    this.mounted = true;
  },

  async beforeMount() {
    this.isLoading = true;
    this.fileTypesRequest({ state: true, to_publish: true });
    await this.fileTypeByIdRequest(85);
    this.content = [this.getFileType.html_header];
    if (this.id) {
      const results = await this.getModelDocumentEdoweb(this.id);
      if (!results) return this.$router.replace("modelos-doe");
      this.fillFields(this.editDocument);
    }
    this.isLoading = false;
  },

  methods: {
    ...mapActions({
      createDocumentEdoweb: "createModelDocumentEdoweb",
      updateDocumentEdoweb: "updateModelDocumentEdoweb",
      fileTypesRequest: "fileTypesRequest",
      getModelDocumentEdoweb: "getModelDocumentEdowebById",
      fileTypeByIdRequest: "fileTypeByIdRequest"
    }),

    setTextHeading(tag) {
      document.execCommand("formatBlock", false, tag);
      const range = window.getSelection().getRangeAt(0);
      const element =
        range.startContainer.nodeType === Node.ELEMENT_NODE
          ? range.startContainer
          : range.startContainer.parentNode;

      element.style.fontSize = "11pt";
      element.style.margin = "0";

      if (tag === "div") {
        element.style.fontWeight = "400";
        document.execCommand("justifyFull");
      }

      if (tag === "h1") {
        element.style.fontWeight = "700";
        element.innerText = element.innerText.toUpperCase();
        document.execCommand("justifyCenter");
      }

      if (tag === "h2") {
        element.style.fontWeight = "400";
        element.innerText = element.innerText.toUpperCase();
        document.execCommand("justifyCenter");
      }

      const elementTextLength = element.childNodes.length;

      const selection = window.getSelection();
      selection.setBaseAndExtent(
        element,
        elementTextLength,
        element,
        elementTextLength
      );

      this.$refs.editor.emit_new_content();
    },

    stylePage(index) {
      return `transform: scale(${this.responsiveZoom}); top: ${
        index
          ? index * (this.page_format_mm[1] * this.responsiveZoom) +
            5.2 * (index + 1)
          : 5.2
      }mm;`;
    },
    actionInputAdd(input) {
      if (!input) return this.undo();
      const exists = this.documentInputList.find(
        input_item => input_item.name === input.name
      );
      if (exists) {
        this.snackToast.open({
          message: `${this.App.EDOWebEditor.FAIL_INPUT_CREATE}.`,
          type: "is-danger",
          duration: 5000
        });
        return this.undo();
      }

      this.documentInputList.push(input);
      const newContent = this.content.map(page =>
        page.replace("{NEW_INPUT}", `{${input.name}}`)
      );

      this.content = newContent;
    },
    openInputModal() {
      document.execCommand("insertText", true, "{NEW_INPUT}");

      this.$buefy.modal.open({
        parent: this,
        component: ModalAddInputDoeVue,
        props: { submit: this.actionInputAdd },
        onCancel: () => this.undo(),
        hasModalCard: true,
        trapFocus: false
      });
    },
    openTableModal() {
      document.execCommand("insertText", true, "{NEW_INPUT}");

      this.$buefy.modal.open({
        parent: this,
        component: ModalAddTableDoe,
        props: {
          inputs: this.documentInputList,
          submit: this.actionInputAdd
        },
        onCancel: () => this.undo(),
        hasModalCard: true,
        trapFocus: false
      });
    },
    openSelectModal() {
      document.execCommand("insertText", true, "{NEW_INPUT}");

      this.$buefy.modal.open({
        parent: this,
        component: ModalAddSelectDoe,
        props: { submit: this.actionInputAdd },
        onCancel: () => this.undo(),
        hasModalCard: true,
        trapFocus: false
      });
    },
    overlay(page, total) {
      const html =
        '<div style="position: absolute; bottom: 8mm; ' +
        (page % 2 ? "right" : "left") +
        ': 10mm">Page ' +
        page +
        " of " +
        total +
        "</div>";

      return html;
    },
    undo() {
      if (this.can_undo) {
        this._mute_next_content_watcher = true;
        this.content = this.content_history[--this.undo_count];
      }
    },
    redo() {
      if (this.can_redo) {
        this._mute_next_content_watcher = true;
        this.content = this.content_history[++this.undo_count];
      }
    },
    resetContentHistory() {
      this.content_history = [];
      this.undo_count = -1;
    },
    async insertPageBreak() {
      document.execCommand("insertParagraph");

      const marker = "###PB###";
      document.execCommand("insertText", false, marker);

      await this.$nextTick();
      const regexp = new RegExp("<(p|div|h\\d)( [^/>]+)*>(<[^/>]+>)*" + marker);
      for (let i = 0; i < this.content.length; i++) {
        const item = this.content[i];
        if (typeof item != "string") continue;
        const match = regexp.exec(item);
        if (match) {
          const tags_open = match[0].slice(0, -marker.length);
          let content_plus_tags_close = item.slice(
            match.index + match[0].length
          );

          if (content_plus_tags_close.indexOf("</") == 0)
            content_plus_tags_close = "<br>" + content_plus_tags_close;
          this.content.splice(
            i,
            1,
            item.slice(0, match.index),
            tags_open + content_plus_tags_close
          );
          return;
        }
      }

      for (let i = 0; i < this.content.length; i++) {
        const item = this.content[i];
        if (typeof item != "string" || item.indexOf(marker) < 0) continue;
        this.content.splice(i, 1, item.replace(marker, ""));
        break;
      }
    },
    validate() {
      const msg = [];
      if (!this.model.name) {
        msg.push(General.PLACEHOLDER.MODEL_NAME);
      }
      if (!this.model.short_name) {
        msg.push(General.PLACEHOLDER.MODEL_NAME_REDUCED);
      }

      if (isEmpty(this.typeModel)) {
        msg.push(General.SELECT_TYPE);
      }
      if (msg.length > 0) {
        this.snackToast.open({
          message: `${msg.join(", ")}.`,
          type: "is-danger",
          duration: 8000
        });
        return false;
      }
      return true;
    },
    async onSubmitDocument() {
      this.isLoading = true;
      if (this.validate()) {
        const inputs = this.documentInputList;
        const document_json = this.content.map(html => ({
          html,
          inputs,
          page_margins: this.page_margins
        }));

        const modelDoc = {
          document_json,
          id: this.id,
          use_type: this.useType,
          ...this.model,
          document_type: this.typeModel.id
        };

        const result = this.id
          ? await this.updateDocumentEdoweb(modelDoc)
          : await this.createDocumentEdoweb(modelDoc);
        result && this.$router.back();
      }
      this.isLoading = false;
    },
    cancel() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: `${this.General.TEXTS.CONFIRM_CANCEL_DOC}?`,
          confirm: () => this.$router.back(),
          buttons: [
            { label: General.YES, isConfirm: false, action: true },
            { label: General.NO, isConfirm: true, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    handleScroll(ev) {
      document.querySelector(".editor-preview").scroll(0, ev.target.scrollTop);
    },
    showApplicabilityModal() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalEditorApplicabilityDoe,
        props: {
          modelParts: {
            all_parts: this.model.all_parts,
            parts: this.model.parts
          },
          modelNature: this.model.nature,
          onSetParts: parts => {
            this.model.nature = "";
            this.model = { ...this.model, ...parts };
          },
          onSetNature: natureObject => {
            this.model.nature = natureObject.typeNature;
            this.model.parts = [];
            this.model.all_parts = false;
          }
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    confirmActionChangeModel() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalWarning,
        props: {
          title: `${this.General.WARNING}!`,
          message: `${App.EDOWebEditor.DROP_MODEL}?`,
          confirm: async () => this.showModalModels(),
          buttons: [
            { label: General.YES, isConfirm: true, action: true },
            { label: General.NO, isConfirm: false, action: false }
          ]
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    showModalModels() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalOfficialModels,
        props: {
          onSelect: model => this.fillFields(model, true)
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    fillFields(model, clone = false) {
      !clone && (this.model.name = model.name);
      !clone && (this.model.short_name = model.short_name);
      const margin = model.document_json[0].page_margins;
      this.typeModel = this.getFileTypes.find(
        option => option.id === model.document_type
      );
      this.content = model.document_json.map(type => type.html);
      this.documentInputList = model.document_json[0].inputs;
      margin &&
        this.margins.map(margin => margin[1]).includes(margin) &&
        (this.page_margins = margin);
      this.model.all_parts = model.all_parts;
      this.model.parts = model.parts;
      this.model.nature = model.nature;
      this.useType = model?.use_type;
    },
    viewDocument() {
      this.$buefy.modal.open({
        parent: this,
        component: ViewEdowebModel,
        props: {
          html: this.replaceContent
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    addField(input) {
      document.execCommand("insertText", true, `{${input.name}}`);
    },

    onChangeType(type) {
      this.typeModel = type;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.main {
  width: fit-content;
  min-width: 100%;
  font-size: 16px;
  overflow: auto;
}
.bar {
  position: sticky;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 1000;
  background: rgba(248, 249, 250, 0.8);
  border-bottom: solid 1px rgb(248, 249, 250);
  backdrop-filter: blur(10px);
  --bar-button-active-color: #188038;
  --bar-button-open-color: #188038;
  --bar-button-active-bkg: #e6f4ea;
  --bar-button-open-bkg: #e6f4ea;
}
.editor-container {
  display: flex;
  flex: 1;
  flex-flow: column;
  background-color: $color-1;
  .editor {
    font-family: "Times New Roman", serif !important;
    font-size: 11pt !important;
    grid-column: 2;
    overflow: scroll;
  }
  .main-container {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: flex-start;
    color: black;
  }
}

.document-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  border-width: 1px;
  border-style: solid;
  border-color: $border-color;
  .actions {
    display: flex;
    flex-wrap: wrap;
    padding: 0.75rem;
    gap: 10px;
  }

  @media screen and (max-width: $tablet) {
    border: none;
    padding: 0 !important;
    .actions {
      flex-direction: column;
      padding-bottom: 15px;
      border-bottom: 1px solid $border-color;
    }
  }
}

.document-info {
  display: flex;
  flex-direction: column-reverse;
  width: 100%;
}

.user-info {
  display: flex;
  flex-direction: row;
  align-items: center;

  @media screen and (max-width: $tablet) {
    flex-direction: column;
    margin-top: 15px;
  }
}

.small-name-container {
  display: flex;

  @media screen and (max-width: $tablet) {
    flex-direction: column;
    gap: 10px;
  }
}

.custom-input-width {
  width: 25vw;

  @media screen and (max-width: $tablet) {
    width: 100%;
  }
}

.button-size {
  min-width: 130px;
}

.action-button {
  border-width: 0px;
}

.editor-preview {
  display: block;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  cursor: default;
  .content-preview {
    position: relative;
    margin: 0;
    padding: 0;
    min-width: 100%;
  }
}

.page-show {
  position: absolute;
  width: 210mm;
  padding: 10mm 15mm;
  min-height: 297mm;
  box-sizing: border-box;
  transform-origin: left top;
  background: var(--page-background, white);
  box-shadow: var(--page-box-shadow, 0 1px 3px 1px rgba(60, 64, 67, 0.15));
  border: var(--page-border);
  border-radius: var(--page-border-radius);
  transition: left 0.3s, top 0.3s;
  overflow: hidden;
}

.control-buttons-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.document-info-continer {
  margin: 5px 5px 0;

  @media screen and (max-width: $tablet) {
    display: flex;
    flex-direction: column;
    margin: 0;
  }
}
</style>

<style lang="scss">
@import "@/assets/scss/variables";

.editor-container {
  .main {
    span.label {
      margin: 0 !important;
    }
  }

  .file-type-dropdown .dropdown-menu {
    z-index: 1001;
  }
}
.custom-width {
  select {
    width: 150px !important;

    @media screen and (max-width: $tablet) {
      width: 100% !important;
    }
  }
}

.toolbar-label {
  .label {
    color: $grey-light;
  }
}
</style>
